import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Lock, Envelope, ArrowRight, ArrowLeft, Eye, EyeSlash } from '@phosphor-icons/react';
import BubbleLogo from '@/components/BubbleLogo';
import GoogleSignInButton from '@/components/GoogleSignInButton';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('email and password required'); return; }
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success('Welcome back.');
      navigate('/home');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === 'invalid credentials'
        ? "That doesn't look right. Check your password — or reset it."
        : (detail || 'Sign-in failed. Try again.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="login-back" className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white mb-4">
          <ArrowLeft size={14} weight="bold" /> Back
        </Link>
        <div className="grad-border p-8 relative">
          <div className="mb-6">
            <BubbleLogo to="/" size="md" />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter">Sign in</h1>
          <p className="text-white/55 mt-1 text-sm">Pick up where the movement left off.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Email</span>
              <div className="mt-1 flex items-center gap-2 px-3 rounded-xl bg-white/5 border border-white/10 focus-within:border-bx-cyan transition">
                <Envelope size={16} className="text-white/60" />
                <input
                  data-testid="login-email"
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@bubblex.app"
                  className="bg-transparent flex-1 py-3 outline-none text-sm" />
              </div>
            </label>
            <label className="block">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-white/65">Password</span>
                <Link to="/forgot-password" data-testid="login-forgot"
                      className="text-xs text-bx-cyan font-bold hover:underline">
                  forgot?
                </Link>
              </div>
              <div className="mt-1 flex items-center gap-2 px-3 rounded-xl bg-white/5 border border-white/10 focus-within:border-bx-cyan transition">
                <Lock size={16} className="text-white/60" />
                <input
                  data-testid="login-password"
                  type={show ? 'text' : 'password'}
                  required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="bg-transparent flex-1 py-3 outline-none text-sm" />
                <button type="button" data-testid="login-show-password"
                        onClick={() => setShow((s) => !s)}
                        aria-label={show ? 'hide password' : 'show password'}
                        className="text-white/60 hover:text-white p-1">
                  {show ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </label>
            <button data-testid="login-submit" disabled={busy}
                    className="w-full btn-primary py-3.5 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? 'Signing in…' : <>Continue <ArrowRight size={16} weight="bold" /></>}
            </button>
          </form>
          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <div className="text-[10px] uppercase tracking-widest text-white/35">or</div>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <div className="mt-4 flex justify-center" data-testid="login-google-wrap">
            <GoogleSignInButton />
          </div>
          <div className="mt-6 text-sm text-white/55 text-center">
            New here?{' '}
            <Link data-testid="login-to-register" to="/register" className="text-bx-cyan font-bold">
              Claim an invite →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
